// fetchDomainTrackerResourceList.js
'use strict';

import fetch from '../resource/customFetch.js';
import handleFetchError from '../resource/handleFetchError.js';
import objectifyArrayById from '../resource/objectifyArrayById.js';
import { domainTracker } from '../resource/debug.js';

import {
  MERGE_OPERATION_DATA,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';
import {
  DOMAIN_TRACKER_DOMAINS,
  DOMAIN_TRACKER_LABEL,
  DOMAIN_TRACKER_TOKEN,
} from '../RemoteConfigKeys.js';

import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getOperationData from '../selector/getOperationData.js';

const log = domainTracker.extend('log:fetchDomainTrackerResourceList');
const errorLog = domainTracker.extend('error:fetchDomainTrackerResourceList');

/**
 * Fetch domain tracker resource list
 * @kind action
 * @param {string} {resourceType} - resource type, ex: 'landing', 'cdn_enc'.
 * @return {Promise} Action promise.
 */
const fetchDomainTrackerResourceList =
  ({ resourceType }) =>
  async (dispatch, getState) => {
    let state = getState();

    const domains = getRemoteConfigData(state, DOMAIN_TRACKER_DOMAINS);
    const label = getRemoteConfigData(state, DOMAIN_TRACKER_LABEL);
    const token = getRemoteConfigData(state, DOMAIN_TRACKER_TOKEN);

    if (!label || !token || !domains?.length || !resourceType) {
      errorLog('missing required data', {
        label,
        token,
        domains,
        resourceType,
      });
      return dispatch({ type: '' });
    }

    // pick using domain
    const storedDomain = getOperationData(
      state,
      ['domainTracker'],
      'usingDomain'
    );
    let usingDomain = null;
    log('start finding using domain', {
      domains,
      storedDomain,
      usingDomain,
      resourceType,
      label,
      token,
    });
    try {
      while (domains.length && !usingDomain && !storedDomain) {
        const domain = domains.shift();
        log('checking domain', { domain });
        const fetchUrl = new URL(
          '/health',
          `${domain.startsWith('http') ? '' : 'https://'}${domain}`
        );
        const response = await fetch(fetchUrl.href);
        if (response.ok) {
          usingDomain = domain;
        }
      }
    } catch (error) {
      errorLog('find using domain error', {
        error,
        domains,
        storedDomain,
      });
    }
    log('using domain found', { usingDomain });
    if (!usingDomain) {
      return dispatch({ type: '' });
    }
    if (usingDomain !== storedDomain) {
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['domainTracker'],
          data: { usingDomain },
        },
      });
    }

    const fetchUrl = new URL(
      '/resources',
      `${usingDomain.startsWith('http') ? '' : 'https://'}${usingDomain}`
    );
    const networkSelectPath = [
      'domainTracker',
      resourceType,
      label,
      'resourceList',
    ];
    const selectPath = ['domainTracker', resourceType, label];
    fetchUrl.searchParams.set('type', resourceType);
    fetchUrl.searchParams.set('label', label);
    fetchUrl.searchParams.set('limit', '100');

    const fetchOptions = {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    dispatch({
      type: SET_NETWORKING_FETCHING,
      payload: { selectPath: networkSelectPath },
    });
    try {
      log('fetching resource list', { fetchUrl: fetchUrl.href });
      let response = await fetch(fetchUrl.href, fetchOptions);

      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl,
        });
      }

      const resourceList = await response.json();
      log('resource list fetched', { fetchUrl: fetchUrl.href, resourceList });

      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath,
          data: {
            resourceList: objectifyArrayById({
              array: resourceList,
              keyAsId: 'url',
            }),
          },
        },
      });

      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath: networkSelectPath },
      });
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath: networkSelectPath, error },
      });
    }
  };

export default fetchDomainTrackerResourceList;
